import React, { useEffect, useRef, useState } from 'react'
import { CHROMECAST_APP_ID } from 'constants.js'
import UnmuteButton from 'components/set/InteractionPlayer/UnmuteButton/UnmuteButton'
import { isNil } from 'lodash'
import useVideoMuted from 'components/set/InteractionPlayer/UnmuteButton/useVideoMuted'
import videojs from 'video.js'
import { VideoPlayerReloadOptions, VideoPlayerWithPlugins } from 'data/VideoPlayer'

interface VideoPlayerVODProps {
    viewerId: string
    videoTitle: string
    src: string
    videoEndedCallback: () => any
    videoStartTime?: number
    disablePauseControls: boolean
    disableChromecast: boolean
    showId: string
}

const VideoPlayerVOD: React.FC<VideoPlayerVODProps> = ({
    viewerId,
    videoTitle,
    src,
    videoEndedCallback,
    disablePauseControls,
    videoStartTime = 0,
    disableChromecast = false,
    showId,
}) => {
    const [player, setPlayer] = useState<VideoPlayerWithPlugins | null>(null)
    const videoNode = useRef(null)
    const [videoMuted, setMuted] = useVideoMuted(player) as [boolean, (muted) => void]

    useEffect(() => {
        // Instantiate Video.js

        if (!player) {
            const videoJsOptions = {
                html5: {
                    hlsjsConfig: {
                        debug: true,
                        liveSyncDuration: 4,
                    },
                },
                techOrder: ['chromecast', 'html5'],
                autoplay: 'any',
                muted: process.env.NODE_ENV === 'development',
                controls: true,
                preload: 'auto',
                fluid: true,
            }

            setPlayer(videojs(videoNode.current, videoJsOptions) as VideoPlayerWithPlugins)
        }
        return () => {
            if (player) {
                player.dispose()
            }
        }
    }, [player])

    useEffect(() => {
        if (player) {
            const data = {
                env_key: process.env.REACT_APP_MUX_ENV_KEY,
                viewer_user_id: viewerId,
                video_title: videoTitle,
                video_variant_id: showId,
            }

            if (isNil(player.activePlugins_)) {
                player.mux({
                    data,
                    debug: false,
                })
                player.airPlay()
                !disableChromecast &&
                    player.chromecast({
                        receiverAppID: CHROMECAST_APP_ID,
                        buttonPositionIndex: -1,
                        addButtonToControlBar: true,
                    })
            } else {
                player.mux.emit('videochange', data)
            }
        }
    }, [player, viewerId, videoTitle, disableChromecast, showId])

    useEffect(() => {
        if (player) {
            player.ready(() => {
                const reloadOptions: VideoPlayerReloadOptions = {}
                reloadOptions.errorInterval = 10
                player.reloadSourceOnError(reloadOptions)
            })

            player.on('error', _ => {
                window.heap.track('Videojs Error in VideoPlayerVOD', { ...player.error() })
            })

            player.on('loadedmetadata', () => {
                if (videoStartTime) {
                    player.currentTime(videoStartTime)
                }
                //eslint-disable-next-line
                player.play()?.catch(console.warn)
            })
            // iPhone/iPad need to play first, then set the time
            // events: https://www.w3.org/TR/html5/embedded-content-0.html#mediaevents
            let initDone = false
            player.on('canplaythrough', function() {
                if (!initDone) {
                    if (videoStartTime) {
                        player.currentTime(videoStartTime)
                        initDone = true
                    }
                }
            })
            player.src({
                src,
                type: 'application/x-mpegurl',
            })
        }
    }, [player, src, videoStartTime])

    useEffect(() => {
        if (player && videoEndedCallback) {
            player.on('ended', videoEndedCallback)
        }
    }, [player, videoEndedCallback])

    // wrap the player in a div with a `data-vjs-player` attribute
    // so videojs won't create additional wrapper in the DOM
    return (
        <div>
            {videoMuted && <UnmuteButton onClick={() => setMuted(false)} />}
            <div data-vjs-player>
                <video
                    id="vod"
                    ref={videoNode}
                    className={`video-js ${disablePauseControls && 'disable-pause'}`}
                    playsInline={true}
                />
            </div>
        </div>
    )
}

export default React.memo(VideoPlayerVOD)
